// ─── Validations — what the agent prepared and may not do alone ─────────────
//
// Owner, docs/agent-v2/SPEC.md « PHASE C »: « NEEDS MY APPROVAL (agent
// prepares, I tap OK in the dashboard): any price change not from the price
// table, discounts, refunds, free reprints, cancelling a paid order, anything
// > 100 DH impact. » And « Dashboard: an "Approvals" tab for these actions ».
//
// Valider = applied through the agent's own applier (worker
// agentApprovalExecutors); a kind with no applier is approved « à faire à la
// main ». Refuser = nothing changes. Under it, the agent's last 50 actions,
// each with Annuler when the album can be put back.
//
// PHASE D (« Approvals tab for Part 4 actions + Part 5 playbooks »): the
// playbooks proposed each Sunday from real chats — Valider / Modifier /
// Refuser. Only validated ones guide the agent (worker lib/playbooks.ts).
import { useQuery } from '@tanstack/react-query';
import { whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Card, CardHeader, EmptyState } from './_ui';
import { AgentActionRow, AGENT_ACTIONS_KEY } from './AgentActionRow';
import { PlaybookRow, PLAYBOOKS_KEY } from './PlaybookRow';
import { splitPlaybooks } from './playbooksView';

export function ApprovalsTab({ onOpenChat }: { onOpenChat?: (chatId: string) => void }) {
  const { t } = useT();
  const pending = useQuery({
    queryKey: [AGENT_ACTIONS_KEY, 'pending'],
    queryFn: () => whatsappApi.agentActions({ pending: true, limit: 100 }),
    refetchInterval: 60_000,
  });
  const recent = useQuery({
    queryKey: [AGENT_ACTIONS_KEY, 'recent'],
    queryFn: () => whatsappApi.agentActions({ limit: 50 }),
    refetchInterval: 120_000,
  });
  const playbooks = useQuery({
    queryKey: [PLAYBOOKS_KEY],
    queryFn: () => whatsappApi.agentPlaybooks(),
    refetchInterval: 300_000,
  });
  const pb = splitPlaybooks(playbooks.data?.items ?? []);
  const waiting = pending.data?.items ?? [];
  const last = recent.data?.items ?? [];
  const missing = pending.data?.table_missing || recent.data?.table_missing;

  return (
    <div className="space-y-4">
      {missing && (
        <p className="text-[12px] text-amber-700" role="status">
          {t('Journal des actions pas encore créé (migration 278) — rien n’est enregistré ici pour le moment.')}
        </p>
      )}

      <Card pad={false}>
        <CardHeader
          title={t('À valider')}
          sub={t('Ce que l’agent a préparé sans le faire : remise, remboursement, réimpression gratuite, annulation d’une commande payée, prix hors grille, ou plus de 100 DH d’impact. Le client a été prévenu qu’une personne confirme.')}
          right={pending.isLoading ? '—' : String(waiting.length)}
        />
        <div className="p-3 space-y-2">
          {pending.isError && <p className="text-[12px] text-red-600" role="alert">{t('Impossible de charger les validations.')}</p>}
          {!pending.isLoading && !pending.isError && waiting.length === 0 && (
            <EmptyState icon={false}>{t('Rien à valider. L’agent n’a rien préparé qui demande votre accord.')}</EmptyState>
          )}
          {waiting.map((it) => <AgentActionRow key={it.id} item={it} onOpenChat={onOpenChat} />)}
        </div>
      </Card>

      <Card pad={false}>
        <CardHeader
          title={t('Playbooks à valider')}
          sub={t('Chaque dimanche, l’agent propose des façons de répondre tirées de conversations réussies (avance payée, plainte résolue). Il ne s’en sert qu’une fois validées. « Modifier » pour écrire la réponse avec vos mots.')}
          right={playbooks.isLoading ? '—' : String(pb.proposed.length)}
        />
        <div className="p-3 space-y-2">
          {playbooks.data?.table_missing && (
            <p className="text-[12px] text-amber-700" role="status">{t('Playbooks pas encore créés (migration 279).')}</p>
          )}
          {playbooks.isError && <p className="text-[12px] text-red-600" role="alert">{t('Impossible de charger les playbooks.')}</p>}
          {!playbooks.isLoading && !playbooks.isError && !playbooks.data?.table_missing && pb.proposed.length === 0 && (
            <EmptyState icon={false}>{t('Aucun playbook à valider. Les prochains arrivent dimanche.')}</EmptyState>
          )}
          {pb.proposed.map((it) => <PlaybookRow key={it.id} item={it} />)}
        </div>
      </Card>

      {(pb.approved.length > 0 || pb.rejected.length > 0) && (
        <Card pad={false}>
          <CardHeader
            title={t('Playbooks validés')}
            sub={t('Ce qui guide l’agent aujourd’hui (les 3 plus proches du message de la cliente, à chaque réponse). « Retirer » pour qu’il ne s’en serve plus.')}
            right={String(pb.approved.length)}
          />
          <div className="p-3 space-y-2">
            {pb.approved.map((it) => <PlaybookRow key={it.id} item={it} />)}
            {pb.rejected.length > 0 && (
              <details className="pt-1">
                <summary className="text-[12px] text-gray-500 cursor-pointer">{t('Refusés récemment')} ({pb.rejected.length})</summary>
                <div className="mt-2 space-y-2">{pb.rejected.map((it) => <PlaybookRow key={it.id} item={it} />)}</div>
              </details>
            )}
          </div>
        </Card>
      )}

      <Card pad={false}>
        <CardHeader
          title={t('Actions de l’agent')}
          sub={t('Les 50 dernières : ce qu’il a fait, quand, et sur quelle conversation. « Annuler » remet l’album comme avant (l’état actuel est gardé dans l’Historique).')}
          right={recent.isLoading ? '—' : String(last.length)}
        />
        <div className="p-3 space-y-2">
          {recent.isError && <p className="text-[12px] text-red-600" role="alert">{t('Impossible de charger les actions.')}</p>}
          {!recent.isLoading && !recent.isError && last.length === 0 && (
            <EmptyState icon={false}>{t('Aucune action enregistrée pour le moment.')}</EmptyState>
          )}
          {last.map((it) => <AgentActionRow key={it.id} item={it} onOpenChat={onOpenChat} />)}
        </div>
      </Card>
    </div>
  );
}
